import React, { useState, useEffect } from 'react';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaSearch, 
  FaDatabase,
  FaFlask,
  FaLink,
  FaFileAlt,
  FaDownload,
  FaTimes,
  FaBookMedical
} from 'react-icons/fa';

interface PubMedQuery {
  query: string;
  maxResults: number;
  filters: {
    dateRange?: string;
    studyType?: string;
    species?: string;
  };
}

const PublicationSearch: React.FC = () => {
  const { token } = useAuth();
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  
  // Research state
  const [pubmedQuery, setPubmedQuery] = useState<PubMedQuery>({
    query: '',
    maxResults: 10,
    filters: {}
  });
  const [researchResults, setResearchResults] = useState<any[]>([]);
  const [selectedResearch, setSelectedResearch] = useState<string[]>([]);
  const [useLLMFilter, setUseLLMFilter] = useState(true);  // LLM filtering enabled by default
  const [selectedDatabase, setSelectedDatabase] = useState<string>('pubmed');  // Database selection
  const [researchModel, setResearchModel] = useState<string>('GPT-4o-mini');  // Research assistant model

  // Load research model configuration
  useEffect(() => {
    const loadConfig = async () => {
      if (!token) return;
      
      try {
        const response = await apiCall(ENDPOINTS.AI.GET_CONFIG, {
          method: 'GET'
        }, token);
        
        if (response.ok) {
          const config = await response.json();
          setResearchModel(config.research_assistant_model || 'GPT-4o-mini');
        }
      } catch (error) {
        console.error('Failed to load AI configuration:', error);
      }
    };
    
    loadConfig();
  }, [token]);

  // Cancel search function
  const handleCancelSearch = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
      setError('Search cancelled');
    }
  };

  // PubMed Search Function
  const handlePubMedSearch = async () => {
    if (!pubmedQuery.query.trim()) return;
    
    // Create new abort controller for this search
    const controller = new AbortController();
    setAbortController(controller);
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Determine which endpoint to use based on selected database
      let endpoint = ENDPOINTS.AI.SEARCH_PUBMED;
      let requestBody: any = {
        query: pubmedQuery.query,
        max_results: pubmedQuery.maxResults,
      };
      
      if (selectedDatabase === 'pbs') {
        endpoint = ENDPOINTS.AI.SEARCH_PBS;
        // PBS doesn't use LLM filter or pubmed-specific filters
      } else if (selectedDatabase === 'tga') {
        endpoint = ENDPOINTS.AI.SEARCH_TGA;
        // TGA doesn't use LLM filter or pubmed-specific filters
      } else if (selectedDatabase === 'pubmed') {
        endpoint = ENDPOINTS.AI.SEARCH_PUBMED;
        requestBody.use_llm_filter = useLLMFilter;
        requestBody = { ...requestBody, ...pubmedQuery.filters };
      } else {
        // 'all' - default to PubMed for now, can be extended later
        endpoint = ENDPOINTS.AI.SEARCH_PUBMED;
        requestBody.use_llm_filter = useLLMFilter;
        requestBody = { ...requestBody, ...pubmedQuery.filters };
      }
      
      const response = await apiCall(
        endpoint,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        },
        token
      );

      if (response.ok) {
        const data = await response.json();
        setResearchResults(data.results || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Search failed');
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Search cancelled by user');
      } else {
        console.error('PubMed search error:', err);
        setError(err.message || 'An error occurred during search');
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  return (
    <div className="h-[calc(100vh-10rem)] overflow-hidden flex flex-col">
      <div className="p-6 pb-0">
        {/* Page Heading */}
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
            <FaBookMedical className="text-primary" />
            Publication Research
          </h1>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="flex-1 px-6 pb-6 min-h-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
        {/* PubMed Search Panel */}
        <div className="card bg-base-200 shadow-xl h-full flex flex-col">
          <div className="card-body flex flex-col min-h-0">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Database</span>
              </label>
              <select 
                className="select select-bordered w-full"
                value={selectedDatabase}
                onChange={(e) => setSelectedDatabase(e.target.value)}
              >
                <option value="all">All Databases</option>
                <option value="pubmed">PubMed</option>
                <option value="tga">TGA ARTG (Australian Medicines)</option>              <option value="pbs">PBS (Australian Medicines)</option>              </select>
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Research Query</span>
              </label>
              <textarea
                className="textarea textarea-bordered h-24"
                placeholder="e.g., chronic pain management multidisciplinary approach"
                value={pubmedQuery.query}
                onChange={(e) => setPubmedQuery({...pubmedQuery, query: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Max Results</span>
                </label>
                <select 
                  className="select select-bordered w-full"
                  value={pubmedQuery.maxResults}
                  onChange={(e) => setPubmedQuery({...pubmedQuery, maxResults: parseInt(e.target.value)})}
                >
                  <option value={5}>5 results</option>
                  <option value={10}>10 results</option>
                  <option value={20}>20 results</option>
                  <option value={30}>30 results</option>
                  <option value={50}>50 results</option>
                  <option value={1000}>All results</option>
                </select>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Date Range</span>
                </label>
                <select 
                  className="select select-bordered w-full"
                  onChange={(e) => setPubmedQuery({
                    ...pubmedQuery, 
                    filters: {...pubmedQuery.filters, dateRange: e.target.value}
                  })}
                >
                  <option value="">All dates</option>
                  <option value="1year">Last year</option>
                  <option value="5years">Last 5 years</option>
                  <option value="10years">Last 10 years</option>
                </select>
              </div>
            </div>

            <div className="form-control">
              <label className="cursor-pointer label justify-start gap-3">
                <input 
                  type="checkbox" 
                  className="checkbox checkbox-primary" 
                  checked={useLLMFilter}
                  onChange={(e) => setUseLLMFilter(e.target.checked)}
                />
                <div>
                  <span className="label-text font-semibold">AI-Powered Filtering</span>
                  <p className="text-xs opacity-70">Use {researchModel} to refine query and filter results for clinical relevance. The AI model used can be changed in the AI Integration Hub settings.</p>
                </div>
              </label>
            </div>

            <div className="card-actions justify-end gap-2">
              {isLoading && (
                <button
                  onClick={handleCancelSearch}
                  className="btn btn-error btn-outline"
                >
                  <FaTimes className="mr-2" />
                  Cancel
                </button>
              )}
              <button
                onClick={handlePubMedSearch}
                disabled={isLoading || !pubmedQuery.query.trim()}
                className="btn btn-info"
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Searching...
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" />
                    Search Databases
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Research Results Panel */}
        <div className="card bg-base-200 shadow-xl h-full flex flex-col min-h-0">
          <div className="card-body flex flex-col min-h-0 p-4">
            <h2 className="card-title text-base-content flex-shrink-0 mb-2">
              <FaFlask className="text-success" />
              Research Results ({researchResults.length})
            </h2>
            
            <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
              {researchResults.length === 0 ? (
                <div className="flex items-center justify-center h-full text-base-content opacity-50">
                  <div className="text-center">
                    <FaDatabase className="text-4xl mx-auto mb-2" />
                    <p>Research results will appear here...</p>
                  </div>
                </div>
              ) : (
                researchResults.map((result, index) => (
                  <div key={index} className="card bg-base-100 shadow-sm">
                    <div className="card-body p-4">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm mt-1"
                          checked={selectedResearch.includes(result.pmid || result.id)}
                          onChange={(e) => {
                            const resultId = result.pmid || result.id;
                            if (e.target.checked) {
                              setSelectedResearch([...selectedResearch, resultId]);
                            } else {
                              setSelectedResearch(selectedResearch.filter(id => id !== resultId));
                            }
                          }}
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{result.title}</h4>
                          
                          {/* PubMed Result Display */}
                          {result.database !== 'TGA ARTG' && (
                            <>
                              <p className="text-xs text-base-content opacity-70">
                                {result.authors?.join(', ') || 'No authors listed'} - {result.journal || 'Unknown journal'} ({result.year || 'N/A'})
                              </p>
                              {result.abstract && (
                                <p className="text-xs mt-1">{result.abstract.substring(0, 150)}...</p>
                              )}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {result.relevance && (
                                  <div className="badge badge-info badge-xs">
                                    Relevance: {(result.relevance * 100).toFixed(0)}%
                                  </div>
                                )}
                                {result.doi && (
                                  <div className="badge badge-ghost badge-xs">
                                    DOI: {result.doi}
                                  </div>
                                )}
                                {result.pmid && (
                                  <div className="badge badge-ghost badge-xs">
                                    PMID: {result.pmid}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                                                    {/* PBS Results Display */}
                          {result.database === 'PBS' && (
                            <>
                              <p className="text-xs text-base-content opacity-80">
                                {result.brand_name && `${result.brand_name} | `}
                                {result.form} | {result.manufacturer}
                              </p>
                              <p className="text-xs mt-1">
                                <strong>PBS Code:</strong> {result.pbs_code} | <strong>Item Code:</strong> {result.item_code}
                              </p>
                              {result.prescriber_code && (
                                <p className="text-xs mt-1">
                                  <strong>Prescriber Code:</strong> {result.prescriber_code}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {result.restriction && (
                                  <div className="badge badge-primary badge-xs">
                                    {result.restriction}
                                  </div>
                                )}
                                {result.authority_required && (
                                  <div className="badge badge-warning badge-xs">
                                    Authority Required
                                  </div>
                                )}
                                {result.schedule && (
                                  <div className="badge badge-info badge-xs">
                                    Schedule: {result.schedule}
                                  </div>
                                )}
                                <div className="badge badge-success badge-xs">
                                  PBS
                                </div>
                                {result.note && (
                                  <div className="badge badge-warning badge-xs">
                                    {result.note}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                                                    {/* TGA ARTG Result Display */}
                          {result.database === 'TGA ARTG' && (
                            <>
                              <p className="text-xs text-base-content opacity-70">
                                {result.sponsor} | {result.product_type} | Status: {result.status}
                              </p>
                              {result.active_ingredients && (
                                <p className="text-xs mt-1">
                                  <strong>Active Ingredients:</strong> {result.active_ingredients}
                                </p>
                              )}
                              {result.indications && (
                                <p className="text-xs mt-1">
                                  <strong>Indications:</strong> {result.indications.substring(0, 150)}{result.indications.length > 150 ? '...' : ''}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {result.artg_id && (
                                  <div className="badge badge-primary badge-xs">
                                    {result.artg_id}
                                  </div>
                                )}
                                <div className="badge badge-success badge-xs">
                                  TGA ARTG
                                </div>
                                {result.note && (
                                  <div className="badge badge-warning badge-xs">
                                    {result.note}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          
                          <div className="flex gap-2 mt-3">
                            {/* PubMed Links */}
                            {result.database !== 'TGA ARTG' && result.database !== 'PBS' && (
                              <>
                                {/* PubMed Link - Always available */}
                                <a
                                  href={`https://pubmed.ncbi.nlm.nih.gov/${result.pmid}/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-xs btn-outline btn-primary"
                                >
                                  <FaLink className="mr-1" /> PubMed
                                </a>
                            
                            {/* DOI Link - Publisher website */}
                            {result.doi && (
                              <a
                                href={`https://doi.org/${result.doi.replace(/^doi:\s*/i, '').trim()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-xs btn-outline btn-secondary"
                              >
                                <FaFileAlt className="mr-1" /> Full Text
                              </a>
                            )}
                            
                            {/* PMC Link - Free PDF if available */}
                            <a
                              href={`https://www.ncbi.nlm.nih.gov/pmc/articles/pmid/${result.pmid}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-xs btn-outline btn-success"
                            >
                              <FaDownload className="mr-1" /> PMC PDF
                            </a>
                            
                            {/* Google Scholar - Alternative source */}
                            <a
                              href={`https://scholar.google.com/scholar?q=${encodeURIComponent(result.title)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-xs btn-outline"
                            >
                              <FaSearch className="mr-1" /> Scholar
                            </a>
                              </>
                            )}
                            
                            {/* PBS Links */}
                            {result.database === 'PBS' && (
                              <>
                                <a
                                  href={result.url || `https://www.pbs.gov.au/medicine/${result.pbs_code || ''}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-xs btn-outline btn-primary"
                                >
                                  <FaLink className="mr-1" /> PBS Details
                                </a>
                                <a
                                  href={`https://www.pbs.gov.au/search?term=${encodeURIComponent(result.title || '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-xs btn-outline btn-secondary"
                                >
                                  <FaSearch className="mr-1" /> PBS Search
                                </a>
                              </>
                            )}
                            
                            {/* TGA ARTG Links */}
                            {result.database === 'TGA ARTG' && (
                              <>
                                <a
                                  href={result.url || `https://www.tga.gov.au/artg`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-xs btn-outline btn-primary"
                                >
                                  <FaLink className="mr-1" /> TGA Details
                                </a>
                                {result.artg_id && (
                                  <a
                                    href={`https://www.tga.gov.au/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-xs btn-outline btn-secondary"
                                  >
                                    <FaFileAlt className="mr-1" /> TGA Search
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {selectedResearch.length > 0 && (
              <div className="alert alert-info">
                <FaLink />
                <span>{selectedResearch.length} studies selected</span>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PublicationSearch;
